import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { DatePicker } from '../utils';
import downloadIcon from '../../assets/images/download.png';
import printIcon from '../../assets/images/share.png';
import { MappedBalanceSheetData, BalanceSheetSection } from '../Accounts/use-balance-sheet';
import { useCurrency } from '../../utils/globalCurrency';

interface BalanceSheetTemplateProps {
  title: string;
  description?: string;
  data: MappedBalanceSheetData | null;
  loading?: boolean;
  onExport?: () => void;
  onPrint?: () => void;
  showDateRange?: boolean;
  startDate?: string;
  endDate?: string;
  onDateRangeChanged?: (startDate?: string, endDate?: string) => void;
  debitSideTitle?: string;
  creditSideTitle?: string;
  emptyMessage?: string;
}

const BalanceSheetTemplate: React.FC<BalanceSheetTemplateProps> = ({
  title,
  description,
  data,
  loading = false,
  onExport,
  onPrint,
  showDateRange = true,
  startDate: propStartDate,
  endDate: propEndDate,
  onDateRangeChanged,
  debitSideTitle = 'DR',
  creditSideTitle = 'CR',
  emptyMessage = 'No balance sheet data available',
}) => {
  const [startDate, setStartDate] = useState(propStartDate || '');
  const [endDate, setEndDate] = useState(propEndDate || '');

    useEffect(() => {
    if ((startDate || endDate) && onDateRangeChanged) {
      onDateRangeChanged(startDate || undefined, endDate || undefined);
    }
  }, [startDate, endDate, onDateRangeChanged]);

  // Use global currency formatting
  const { formatWhole } = useCurrency();

  const formatCurrency = (amount: number) => {
    return formatWhole(Math.abs(amount));
  };

  const getFormattedAmount = (amount: number) => {
    const formatted = formatCurrency(amount);
    return {
      display: formatted,
      title: formatted
    };
  };

  const renderSection = (section: BalanceSheetSection) => {
    return (
      <div key={section.title} className="mb-4">
        {section.title && (
          <div className="font-medium text-sm mb-2 text-grey_dark">{section.title}</div>
        )}
        {section.items.map((item, index) => {
          const isSubHeader = item.name.endsWith(':');
          const isSubItem = item.isSubItem;

          return (
            <div
              key={`${item.name}-${index}`}
              className={`grid grid-cols-12 gap-2 py-1 ${isSubItem ? 'ml-8' : ''}`}
            >
              <div className={`${isSubItem ? 'col-span-8' : 'col-span-8'} text-sm ${isSubHeader ? 'text-grey_dark font-medium' : ''}`}>
                {item.name}
              </div>
                            <div className="col-span-4 text-right text-sm">
                {!isSubHeader && (
                  <span 
                    className="dashboard-figure"
                    title={formatCurrency(item.amount)}
                  >
                    {formatCurrency(item.amount)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSide = (isDebitSide: boolean) => {
    const sideData = isDebitSide ? data?.debitSide : data?.creditSide;
    const sideTitle = isDebitSide ? debitSideTitle : creditSideTitle;

    return (
      <div className="flex-1 flex flex-col">
        <div className={`${isDebitSide ? 'text-start' : 'text-end'} mb-2 text-dark text-sm font-medium`}>
          {sideTitle}
        </div>

        <div className="grid grid-cols-12 gap-2 mb-3 pb-2 border-b border-grey_light text-grey_dark text-sm">
          <div className="col-span-8">particular</div>
          <div className="col-span-4 text-right">amt</div>
        </div>

        <div className="flex-1">
          {!sideData || sideData.sections.length === 0 ? (
            <div className="text-center py-6 text-grey_dark text-sm">
              {emptyMessage}
            </div>
          ) : (
            <div>
              {sideData.sections.map((section, index) => (
                <React.Fragment key={`section-${index}`}>
                  {renderSection(section)}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {sideData && sideData.sections.length > 0 && (
                    <div className="grid grid-cols-12 gap-2 py-2 mt-4">
            <div className="col-span-8"></div>
            <div className="col-span-4 text-right text-sm">
              <span 
                className="dashboard-figure"
                title={formatCurrency(sideData.total)}
              >
                {formatCurrency(sideData.total)}
              </span>
            </div>
          </div>
        )}

                <div className="py-3 border-y border-grey_light">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-8 text-right text-sm">Total:</div>
            <div className="col-span-4 text-right text-sm font-medium">
              <span 
                className="dashboard-figure"
                title={formatCurrency(sideData?.total || 0)}
              >
                {formatCurrency(sideData?.total || 0)}
              </span>
            </div>
          </div>
        </div>

        <div className="py-2">
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-8"></div>
            <div className="col-span-4 text-right text-sm text-grey_dark">_</div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
        <div className="max-w-7xl mx-auto">
          <div className="p-6 bg-light rounded-lg flex justify-center items-center">
            <div className="text-grey_dark">Loading balance sheet...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="p-6 pb-3 bg-light rounded-t-lg flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2">
          <div className="w-full sm:w-auto mt-2">
            <h3 className="text-xl">{title}</h3>
            {description && (
              <p className="text-grey_dark text-sm mt-1">{description}</p>
            )}
          </div>
          <div className="flex gap-2 mt-4 sm:mt-0 text-sm">
            {onExport && (
              <Button
                onClick={onExport}
                variant="google"
                className="flex items-center text-grey_dark"
              >
                <img
                  src={downloadIcon}
                  alt="Export"
                  className="object-contain w-6 h-6 mr-2"
                />
                Export
              </Button>
            )}
            {onPrint && (
              <Button
                onClick={onPrint}
                variant="google"
                className="flex items-center text-grey_dark"
              >
                <img
                  src={printIcon}
                  alt="Print"
                  className="object-contain w-6 h-6 mr-2"
                />
                Print
              </Button>
            )}
          </div>
        </div>

        {/* Date Range Selector */}
        {showDateRange && (
          <div className="bg-light rounded-lg p-6 mb-2">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="flex justify-between">
                <div className="mr-2">
                  <DatePicker
                    id="start-date"
                    value={startDate}
                    onChange={setStartDate}
                    label="Start Date"
                    type="date"
                    className="mr-3"
                  />
                </div>
                <div className="ml-2">
                  <DatePicker
                    id="end-date"
                    value={endDate}
                    onChange={setEndDate}
                    label="End Date"
                    type="date"
                  />
                </div>
              </div>
              <div className="text-sm text-grey_dark">
                {/* Entry count if needed */}
              </div>
            </div>
          </div>
        )}

        {/* Balance Sheet Table */}
        <div className="bg-light rounded-lg p-4 sm:p-6 mb-4">
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-6">
            {renderSide(true)}
            <div className="hidden lg:block w-px bg-grey_light"></div>
            {renderSide(false)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceSheetTemplate;
